/**
 * Custom RX protocol event types for postMessage communication.
 * Protocol spec: https://cloud.wiki.aligntech.com/wiki/spaces/iKMSARCH/pages/214524362/Custom+RX+simulator
 */

import { ProcedureTypeEnum } from '@core/procedure-helpers/models/procedure-type.enum';
import { ProcedureEnum } from '@core/procedure-helpers/models/procedure.enum';
import { TeethNumberingSystem } from '@modules/rx-intuitive-teeth-orchestrator/models';

export enum CustomRxEventType {
	RxUIReady = 'RxUIReady',
	RxInit = 'RxInit',
	RxUpdate = 'RxUpdate',
	RxComplete = 'RxComplete',
	RxExtensionClose = 'RxExtensionClose',
	TokenRefreshRequest = 'TokenRefreshRequest',
	TokenRefreshResponse = 'TokenRefreshResponse',
	RxStatus = 'RxStatus',
	RxError = 'RxError'
}

export type EventPublisher = 'iTeroRx';

export interface EventHeader {
	Id: string;
	Type: CustomRxEventType;
	SchemaVersion: string;
	EventTime: string;
	publisher: EventPublisher;
}

export interface DoctorInfo {
	Id?: string;
	Name?: string;
}

export interface CompanyInfo {
	Id?: string;
	Name?: string;
}

export type HeaderPlatformType = 'SCANNER' | 'WEB';

export interface ContextHeader {
	RxId: string;
	PatientGuid: string;
	Doctor?: DoctorInfo;
	Company?: CompanyInfo;
	Platform?: HeaderPlatformType;
}

export interface SessionHeader {
	Jwt?: string;
	tokenType?: string;
}

export interface CustomRxEvent<TData = unknown> {
	EventHeader: EventHeader;
	ContextHeader?: ContextHeader;
	SessionHeader?: SessionHeader;
	Data: TData;
}

export interface BridgeContract {
	BridgeIndex: string;
	FromToothId: string;
	ToToothId: string;
}

export interface PatientData {
	ChartNumber: string;
	FirstName: string;
	LastName: string;
	Gender?: string;
	DateOfBirth?: string;
}

export interface ProcedureData {
	Procedure: string;
	Type?: string;
	CustomFields?: Record<string, unknown>;
}

export interface ToothIndication {
	ToothNumber: string;
	Indication: string;
	CustomFields?: Record<string, unknown>;
}

export type ToothIdSystem = 'FDI' | 'ADA' | 'QUADRANT';
export type ShadeSystem = 'VITA_CLASSICAL' | 'VITA_3D_MASTER' | 'OTHER';

/** Maps {@link TeethNumberingSystem} numeric values to the protocol string. */
export const toothIdSystemFromUserSetting: ReadonlyMap<TeethNumberingSystem, ToothIdSystem> = new Map([
	[TeethNumberingSystem.FDI, 'FDI'],
	[TeethNumberingSystem.ADA, 'ADA'],
	[TeethNumberingSystem.Quadrant, 'QUADRANT']
]);

/** Maps shade system numeric IDs (from {@link UserSettings.ShadeSystemId}) to the protocol string. */
export const shadeSystemFromUserSetting: ReadonlyMap<number, ShadeSystem> = new Map([
	[3000, 'VITA_CLASSICAL'],
	[3001, 'VITA_3D_MASTER'],
	[-1, 'OTHER']
]);

export interface UserPreferences {
	language: string;
	dateFormat: string;
	toothIdSystem: ToothIdSystem;
	shadeSystem: ShadeSystem;
}

export interface RxUIReadyData {
	Version: string;
}

/**
 * Possible values for the DentureDetails list.
 * Only enabled options are included in the array sent to the Custom RX.
 */
export type DentureDetailValue = 'Upper' | 'Lower' | 'DentureCopyScan';

export interface RxInitData {
	Patient: PatientData;
	Procedure: ProcedureData;
	Teeth: ToothIndication[];
	Bridges: BridgeContract[];
	DentureDetails?: DentureDetailValue[];
	UserPrefs: UserPreferences;
}

export interface RxUpdateData {
	Patient?: Partial<PatientData>;
	Procedure?: ProcedureData;
	Teeth?: ToothIndication[];
	Bridges?: BridgeContract[];
	DentureDetails?: DentureDetailValue[];
	UserPrefs?: Partial<UserPreferences>;
}

export type RxCompleteStatus = 'SUCCESS' | 'FAIL' | 'NOT_READY';

export interface RxCompleteReason {
	Code: string;
	Message: string;
}

export interface RxCompleteData {
	Status: RxCompleteStatus;
	FullCustomRx?: Record<string, unknown>;
	Reason?: RxCompleteReason;
}

export type CloseReason = 'USER_CANCELLED' | 'TIMEOUT' | 'ERROR' | 'NAVIGATE_AWAY' | 'FALLBACK';

export interface RxExtensionCloseData {
	Reason: CloseReason;
	Details?: string;
}

export interface TokenRefreshRequestData {
	Reason: string;
}

export interface TokenRefreshError {
	Code: string;
	Message: string;
}

export interface TokenRefreshResponseData {
	Success: boolean;
	Error?: TokenRefreshError;
}

export type RxStatusValue =
	| 'IN_PROGRESS'
	| 'WAITING_USER'
	| 'SAVING'
	| 'ACTIVE'
	| 'CLOSING'
	| 'CLOSED'
	| 'READY_TO_CLOSE'
	| 'NOT_READY_TO_CLOSE'
	| 'ERROR';

export interface RxStatusData {
	Status: RxStatusValue;
	Step?: string;
	Info?: string;
}

/** Maps ProcedureID values to their canonical names per the Custom RX protocol. */
export const procedureIdToName: ReadonlyMap<ProcedureEnum, string> = new Map([
	[ProcedureEnum.StudyModel_iRecord, 'Study Model / iRecord'],
	[ProcedureEnum.Invisalign, 'Invisalign'],
	[ProcedureEnum.ImplantPlanning, 'Implant Planning'],
	[ProcedureEnum.FixedRestorative, 'Fixed Restorative'],
	[ProcedureEnum.Denture_Removable, 'Denture/Removable'],
	[ProcedureEnum.Appliance, 'Appliance'],
	[ProcedureEnum.InvisalignART, 'Invisalign ART']
]);

/** Maps ProcedureTypeID values to their canonical names per the Custom RX protocol. */
export const procedureTypeIdToName: ReadonlyMap<ProcedureTypeEnum | number, string> = new Map([
	[ProcedureTypeEnum.OrthoModelICast, 'Ortho Model/iCast'],
	[ProcedureTypeEnum.InvisalignAligners, 'Invisalign Aligners'],
	[ProcedureTypeEnum.InvisalignFirstAligners, 'Invisalign First Aligners'],
	[ProcedureTypeEnum.InvisalignFirstPalatalExpander, 'Invisalign First Palatal Expander'],
	[ProcedureTypeEnum.ViveraRetainer, 'Vivera Retainer'],
	[ProcedureTypeEnum.InvisalignRetainer, 'Invisalign Retainer'],
	[ProcedureTypeEnum.NightGuard, 'Night guard'],
	[ProcedureTypeEnum.BiteSplint, 'Bite Splint'],
	[ProcedureTypeEnum.SportsMouthGuard, 'Sports/Mouth guard'],
	[ProcedureTypeEnum.ApneaSleepAppliance, 'Apnea/Sleep Appliance'],
	[ProcedureTypeEnum.ClearAligners, 'Clear aligners'],
	[ProcedureTypeEnum.OrthoAppliance, 'Ortho Appliance'],
	[ProcedureTypeEnum.SurgicalGuideToothSupported, 'Surgical guide Tooth supported'],
	[ProcedureTypeEnum.SurgicalGuideTissueSupported, 'Surgical guide Tissue Supported'],
	[ProcedureTypeEnum.SurgicalGuideBoneSupported, 'Surgical guide Bone supported'],
	[ProcedureTypeEnum.FixedRestorativeHidden, 'Fixed restorative hidden'],
	[ProcedureTypeEnum.PartialDentureFramework, 'Partial Denture/Framework'],
	[ProcedureTypeEnum.ImmediateDenture, 'Immediate Denture'],
	[ProcedureTypeEnum.FullDentureTissueBased, 'Full Denture tissue based'],
	[ProcedureTypeEnum.FullDentureImplantBased, 'Full Denture implant based'],
	[ProcedureTypeEnum.SurgicalPlanningOnlyNoGuide, 'Surgical planning only - No guide'],
	[ProcedureTypeEnum.FullImmediateDenture, 'Full Immediate Denture'],
	[ProcedureTypeEnum.PartialDentureAcrylic, 'Partial denture acrylic']
]);
